(function () {
  const MODAL_ID = 'sm-size-guide-modal';
  const CONTENT_ID = 'sm-size-guide-content';
  const DEFAULT_PAGE_PATH = '/pages/size-chart';
  const CACHE_PREFIX = 'sizeChartCache:';
  const LINK_SELECTOR = '[data-size-chart-link]';

  const memoryCache = new Map();
  const inflight = new Map();
  const prefetched = new Set();

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function getContent() {
    return document.getElementById(CONTENT_ID);
  }

  function openModal(modal, opener) {
    if (!modal) return;
    if (typeof modal.open === 'function') {
      modal.open(opener || null);
      return;
    }
    modal.setAttribute('open', '');
    modal.classList.add('open');
  }

  function handleize(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeAnchor(anchor) {
    if (!anchor) return '';
    const a = anchor.toString().trim();
    if (!a) return '';
    return a[0] === '#' ? a : `#${a}`;
  }

  function safeCssEscape(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
    return value.replace(/([ #;?%&,.+*~'"!^$\[\]()=>|\/\\])/g, '\\$1');
  }

  function scrollToAnchor(container, anchor) {
    if (!container) return;
    const a = normalizeAnchor(anchor);
    if (!a) return;

    const id = a.slice(1);
    const sel = `#${safeCssEscape(id)}`;
    const target = container.querySelector(sel);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }

  function getFitValue(link, fitOptionName) {
    if (!fitOptionName) return '';

    const picker = link.closest('variant-picker') || link.closest('form') || document;
    if (!picker) return '';

    const optionValue = fitOptionName.toString();
    const optionSel = `[data-option="${safeCssEscape(optionValue)}"]`;
    const optionRoot = picker.querySelector(optionSel);
    if (!optionRoot) return '';

    const checkedRadio = optionRoot.querySelector('input[type="radio"]:checked');
    if (checkedRadio && checkedRadio.value) return checkedRadio.value;

    const selectedCustomOption = optionRoot.querySelector('.custom-select__option[aria-selected="true"]');
    if (selectedCustomOption) {
      return (selectedCustomOption.getAttribute('data-value') || selectedCustomOption.textContent || '').trim();
    }

    const nativeSelect = optionRoot.querySelector('select');
    if (nativeSelect && nativeSelect.value) return nativeSelect.value;

    return '';
  }

  function cacheKey(basePath, anchor) {
    return `${CACHE_PREFIX}${basePath}|${anchor}`;
  }

  function baseKey(basePath) {
    return `${CACHE_PREFIX}${basePath}|`;
  }

  function readSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      return;
    }
  }

  function readCache(basePath, anchor) {
    const fullKey = cacheKey(basePath, anchor);
    if (memoryCache.has(fullKey)) return memoryCache.get(fullKey);

    let cached = readSession(fullKey);
    if (cached) {
      memoryCache.set(fullKey, cached);
      return cached;
    }

    const fallbackKey = baseKey(basePath);
    if (memoryCache.has(fallbackKey)) {
      cached = memoryCache.get(fallbackKey);
      memoryCache.set(fullKey, cached);
      writeSession(fullKey, cached);
      return cached;
    }

    cached = readSession(fallbackKey);
    if (cached) {
      memoryCache.set(fallbackKey, cached);
      memoryCache.set(fullKey, cached);
      writeSession(fullKey, cached);
      return cached;
    }

    return null;
  }

  function writeCache(basePath, anchor, html) {
    const fullKey = cacheKey(basePath, anchor);
    const fallbackKey = baseKey(basePath);

    memoryCache.set(fullKey, html);
    memoryCache.set(fallbackKey, html);
    writeSession(fullKey, html);
    writeSession(fallbackKey, html);
  }

  function resolveLink(link) {
    const href = (link.getAttribute('href') || link.getAttribute('data-sm-size-guide-url') || '').trim();
    let hrefUrl = null;

    if (href) {
      try {
        hrefUrl = new URL(href, window.location.origin);
      } catch (e) {
        hrefUrl = null;
      }
    }

    const pagePath = (link.getAttribute('data-sm-size-guide-page') || (hrefUrl && hrefUrl.pathname) || DEFAULT_PAGE_PATH).trim();
    let anchor = normalizeAnchor(link.getAttribute('data-sm-size-guide-hash') || (hrefUrl && hrefUrl.hash) || '');

    if (anchor && anchor.indexOf('#suits') === 0) {
      const fitOptionName = (link.getAttribute('data-sm-size-guide-fit-option') || '').trim();
      const fitValue = getFitValue(link, fitOptionName);
      anchor = fitValue ? `#suits-${handleize(fitValue)}` : '#suits';
    }

    const baseUrl = new URL(pagePath, window.location.origin);
    if (hrefUrl && hrefUrl.search) {
      baseUrl.search = hrefUrl.search;
    } else if (!baseUrl.search && window.location.search) {
      baseUrl.search = window.location.search;
    }

    const basePath = `${baseUrl.pathname}${baseUrl.search}`;
    const fullUrl = `${basePath}${anchor}`;

    if (link.tagName === 'A') {
      link.setAttribute('href', fullUrl);
    }

    return { fullUrl, basePath, anchor };
  }

  function extractContent(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content =
      doc.querySelector('[data-size-chart-content]') ||
      doc.querySelector('main .page-width') ||
      doc.querySelector('main') ||
      doc.body;

    if (!content) return '';

    content.querySelectorAll('script').forEach((script) => script.remove());
    return content.outerHTML || content.innerHTML || '';
  }

  function wrapTables(container) {
    container.querySelectorAll('table').forEach((table) => {
      if (table.closest('.scrollable-table')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'scrollable-table';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  function loadSizeChart(resolved) {
    const cached = readCache(resolved.basePath, resolved.anchor);
    if (cached) {
      return Promise.resolve({ html: cached, anchor: resolved.anchor, cached: true });
    }

    if (inflight.has(resolved.basePath)) {
      return inflight.get(resolved.basePath).then((html) => {
        writeCache(resolved.basePath, resolved.anchor, html);
        return { html, anchor: resolved.anchor, cached: false };
      });
    }

    const request = fetch(resolved.basePath, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Request failed');
        return response.text();
      })
      .then((html) => {
        const extracted = extractContent(html);
        if (!extracted) throw new Error('No content');
        writeCache(resolved.basePath, resolved.anchor, extracted);
        return extracted;
      })
      .finally(() => {
        inflight.delete(resolved.basePath);
      });

    inflight.set(resolved.basePath, request);

    return request.then((html) => ({ html, anchor: resolved.anchor, cached: false }));
  }

  function renderContent(content, html, anchor) {
    content.innerHTML = html;
    wrapTables(content);
    requestAnimationFrame(() => scrollToAnchor(content, anchor));
  }

  function showLoading(content) {
    content.innerHTML = '<p class="sm-size-guide__loading">Loading...</p>';
  }

  function preloadLink(link) {
    const resolved = resolveLink(link);
    const key = cacheKey(resolved.basePath, resolved.anchor);

    if (prefetched.has(key)) return;
    prefetched.add(key);

    loadSizeChart(resolved).catch(() => {
      prefetched.delete(key);
    });
  }

  document.addEventListener('click', function (e) {
    const link = e.target.closest(LINK_SELECTOR);
    if (!link) return;

    const modal = getModal();
    const content = getContent();
    if (!modal || !content) return;

    const resolved = resolveLink(link);
    e.preventDefault();

    const cached = readCache(resolved.basePath, resolved.anchor);
    if (cached) {
      renderContent(content, cached, resolved.anchor);
      openModal(modal, link);
      return;
    }

    showLoading(content);
    openModal(modal, link);

    loadSizeChart(resolved)
      .then((result) => {
        renderContent(content, result.html, result.anchor);
      })
      .catch(() => {
        window.location.href = resolved.fullUrl;
      });
  });

  document.addEventListener('DOMContentLoaded', function () {
    const links = Array.from(document.querySelectorAll(LINK_SELECTOR));
    if (!links.length) return;

    const modal = getModal();
    if (modal) {
      const modalWindow = modal.querySelector('.modal__window');
      if (modalWindow) {
        modalWindow.addEventListener('click', (event) => {
          if (event.target.closest('.js-close-modal')) return;
          event.stopPropagation();
        });
      }

      modal.addEventListener('click', (event) => {
        if (event.target !== modal) return;
        if (!modal.hasAttribute('open')) return;

        if (typeof modal.close === 'function') {
          modal.close();
        } else {
          modal.removeAttribute('open');
          modal.classList.remove('open');
        }
      });
    }

    links.forEach((link) => {
      link.addEventListener('mouseenter', () => preloadLink(link));
      link.addEventListener('focus', () => preloadLink(link));
      link.addEventListener('touchstart', () => preloadLink(link), { passive: true });
    });
  });
})();
